// ── 급여 직원 목록 (모바일 카드 / 데스크톱 테이블) ─────────────────────────
// PayrollMonthly.jsx에서 분리 — 직원별 급여 기록 렌더링 담당
// 컬럼: 이름|고정기본급|인센티브|지급합계|출근일|근태공제|주휴수당공제|공제합계|실수령액|상태

import React from 'react';
import { printPayslip } from './payslipPrint';

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '0';

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
const btnOutline = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 10, cursor: 'pointer',
};
// 상태 뱃지 색상 맵 (표준 톤)
const STATUS_MAP = {
  confirmed:  { label: '확정',    bg: '#dcfce7', color: '#15803d' },
  calculated: { label: '계산완료', bg: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)' },
  draft:      { label: '임시',    bg: '#f3f4f6', color: '#6b7280' },
};
const badgeBase = { fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700, whiteSpace: 'nowrap' };
const warningBadgeMeta = {
  danger: { label: '위험', background: 'rgba(220,38,38,0.10)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.35)' },
  warning: { label: '주의', background: 'rgba(217,119,6,0.12)', color: '#d97706', border: '1px solid rgba(217,119,6,0.35)' },
  info: { label: '근거', background: 'rgba(37,99,235,0.08)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.30)' },
};
const warningBadgeOrder = ['danger', 'warning', 'info'];

// ── 레코드 파생값 계산 ──────────────────────────────────────────────────────
function derive(r) {
  const inc = Number(r.incentive_amount) || 0;
  const unusedAnnual = Number(r.unused_annual_leave_allowance) || 0;
  const attDed = Math.abs(Number(r.attendance_deduction_amount) || 0);
  const whDed = Math.abs(Number(r.weekly_holiday_deduction) || 0);
  const fixedBase = Number(r.total_earning) - inc - unusedAnnual + attDed + whDed;
  return { inc, unusedAnnual, fixedBase, attDed, whDed };
}

// ── 출근일 표시 ──────────────────────────────────────────────────────────────
// 해당없음(출근 없음) / "18일"(actual) / "18일 (13/5)"(projected, /5 흐리게)
function WorkDays({ r, calcMode }) {
  if (!r.attendance_time_type) return <span style={{ color: 'var(--text-muted)' }}>해당없음</span>;
  const wd = Number(r.work_days) || 0;
  const paid = Number(r.paid_leave_days) || 0;
  const paidLabel = paid > 0 ? <span style={{ fontSize: 10, color: '#2563eb' }}> ({paid}일)</span> : null;
  // actual 모드이거나 actual_work_days가 NULL(기존 데이터 호환)이면 단순 표시
  if (calcMode !== 'projected' || r.actual_work_days == null) return <span>{wd}일{paidLabel}</span>;
  const actual = Number(r.actual_work_days) || 0;
  const projected = wd - actual;
  if (projected <= 0) return <span>{wd}일{paidLabel}</span>;
  // projected 모드: "18일 (13/5)" — "/5" 부분 muted
  return (
    <span>{wd}일{' '}
      <span style={{ fontSize: 10 }}>
        ({actual}<span style={{ opacity: 0.5 }}>/{projected}</span>)
      </span>
      {paidLabel}
    </span>
  );
}

// ── 상태 뱃지 ────────────────────────────────────────────────────────────────
function Badge({ status, adjusted }) {
  if (adjusted) return <span style={{ ...badgeBase, background: '#fef3c7', color: '#d97706' }}>수정됨</span>;
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return <span style={{ ...badgeBase, background: s.bg, color: s.color }}>{s.label}</span>;
}

function countWarningsBySeverity(warnings = []) {
  return warnings.reduce((acc, warning) => {
    const severity = warningBadgeMeta[warning?.severity] ? warning.severity : 'warning';
    acc[severity] = (acc[severity] || 0) + 1;
    return acc;
  }, {});
}

function WarningBadge({ warnings = [] }) {
  const counts = countWarningsBySeverity(warnings);
  const severities = warningBadgeOrder.filter((severity) => counts[severity] > 0);
  if (!severities.length) return null;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {severities.map((severity) => {
        const meta = warningBadgeMeta[severity];
        return (
          <span key={severity} style={{ ...badgeBase, background: meta.background, color: meta.color, border: meta.border }}>
            {meta.label} {counts[severity]}
          </span>
        );
      })}
    </span>
  );
}

// ── 모바일 카드형 급여 목록 ──────────────────────────────────────────────────
function MobileRecordCards({ records, period, totalEarn, totalDed, totalNet, companyName, onSelect }) {
  const calcMode = period?.calculation_mode;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {records.map((r) => {
        const { inc, unusedAnnual, fixedBase, attDed, whDed } = derive(r);
        return (
          <div key={r.id} style={{ ...cardStyle, padding: '14px 16px', cursor: 'pointer' }} onClick={() => onSelect(r.id)}>
            {/* 직원명 + 상태 뱃지 + 명세서 버튼 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{r.user_name}</span>
                <Badge status={period.status} adjusted={r.is_adjusted} />
                <WarningBadge warnings={r.warnings || []} />
              </div>
              <button style={btnOutline} onClick={(e) => { e.stopPropagation(); printPayslip(r.id, period, companyName); }}>명세서</button>
            </div>
            {/* 금액 2×3 그리드 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>고정기본급</span>
                <span style={{ color: 'var(--text-sub)', fontWeight: 600 }}>{fmt(fixedBase)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>인센티브</span>
                <span style={{ color: inc > 0 ? '#2563eb' : 'var(--text-sub)', fontWeight: 600 }}>{fmt(inc)}</span>
              </div>
              {unusedAnnual > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>미사용연차</span>
                  <span style={{ color: '#047857', fontWeight: 600 }}>{fmt(unusedAnnual)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>근태공제</span>
                <span style={{ color: attDed > 0 ? '#dc2626' : 'var(--text-sub)', fontWeight: 600 }}>{fmt(attDed)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>주휴공제</span>
                <span style={{ color: whDed > 0 ? '#dc2626' : 'var(--text-sub)', fontWeight: 600 }}>{fmt(whDed)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#16a34a' }}>지급합계</span>
                <span style={{ color: '#16a34a', fontWeight: 700 }}>{fmt(r.total_earning)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>출근일(유급휴가)</span>
                <span style={{ color: 'var(--text-sub)', fontWeight: 600 }}><WorkDays r={r} calcMode={calcMode} /></span>
              </div>
            </div>
            {/* 공제합계 + 실수령액 */}
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, padding: '6px 10px', background: 'rgba(220,38,38,0.06)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>공제합계</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#dc2626' }}>{fmt(r.total_deduction)}</span>
              </div>
              <div style={{ flex: 1, padding: '6px 10px', background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>실수령액</span>
                <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--std-accent-text, #1d7de6)' }}>{fmt(r.net_pay)}원</span>
              </div>
            </div>
          </div>
        );
      })}
      {/* 합계 카드 */}
      <div style={{ ...cardStyle, padding: '12px 16px', background: 'var(--bg-subtle)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>지급 합계</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#16a34a' }}>{fmt(totalEarn)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>공제 합계</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#dc2626' }}>{fmt(totalDed)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>실수령 합계</div>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--std-accent-text, #1d7de6)' }}>{fmt(totalNet)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 데스크톱 테이블형 급여 목록 ──────────────────────────────────────────────
function DesktopRecordTable({ records, period, totalEarn, totalDed, totalNet, companyName, onSelect }) {
  const calcMode = period?.calculation_mode;
  // 컬럼별 합계 계산
  const totals = records.reduce((acc, r) => {
    const { inc, unusedAnnual, fixedBase, attDed, whDed } = derive(r);
    acc.fixedBase += fixedBase; acc.inc += inc; acc.unusedAnnual += unusedAnnual; acc.attDed += attDed; acc.whDed += whDed;
    return acc;
  }, { fixedBase: 0, inc: 0, unusedAnnual: 0, attDed: 0, whDed: 0 });

  const showUnusedAnnual = totals.unusedAnnual > 0;
  const headers = ['이름', '고정기본급', '인센티브', '근태공제', '주휴공제', ...(showUnusedAnnual ? ['미사용연차'] : []), '지급합계', '출근일(유급휴가)', '공제합계', '실수령액', '상태', ''];
  // 머리글 — 디자인 표준(stdTh 톤: 검정 띠 + 흰 글씨), 정렬만 컬럼별 유지
  const th = (h) => ({ padding: '10px 6px', textAlign: h === '이름' ? 'left' : (h === '' ? 'center' : 'right'), fontWeight: 600, color: 'rgba(255,255,255,0.92)', background: 'var(--std-thead, #0f172a)', fontSize: 11, whiteSpace: 'nowrap' });
  const td = (align = 'right', color = 'var(--text-sub)', fw = 400) => ({ padding: '8px 6px', textAlign: align, color, fontWeight: fw });

  return (
    <div style={{ ...cardStyle, padding: 0, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {headers.map((h) => <th key={h} style={th(h)}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {records.map((r) => {
            const { inc, unusedAnnual, fixedBase, attDed, whDed } = derive(r);
            return (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--bg-subtle)', cursor: 'pointer', transition: 'background 0.1s' }}
                onClick={() => onSelect(r.id)}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <td style={td('left', 'var(--text)', 700)}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {r.user_name}
                    <WarningBadge warnings={r.warnings || []} />
                  </span>
                </td>
                <td style={td()}>{fmt(fixedBase)}</td>
                <td style={td('right', inc > 0 ? '#2563eb' : 'var(--text-sub)')}>{fmt(inc)}</td>
                <td style={td('right', attDed > 0 ? '#dc2626' : 'var(--text-sub)')}>{fmt(attDed)}</td>
                <td style={td('right', whDed > 0 ? '#dc2626' : 'var(--text-sub)')}>{fmt(whDed)}</td>
                {showUnusedAnnual && (
                  <td style={td('right', unusedAnnual > 0 ? '#047857' : 'var(--text-sub)')}>{fmt(unusedAnnual)}</td>
                )}
                <td style={td('right', '#16a34a', 700)}>{fmt(r.total_earning)}</td>
                <td style={td()}><WorkDays r={r} calcMode={calcMode} /></td>
                <td style={td('right', '#dc2626', 700)}>{fmt(r.total_deduction)}</td>
                <td style={td('right', 'var(--std-accent-text, #1d7de6)', 900)}>{fmt(r.net_pay)}</td>
                <td style={td('center')}><Badge status={period.status} adjusted={r.is_adjusted} /></td>
                <td style={td('center')}>
                  <button style={btnOutline} onClick={(e) => { e.stopPropagation(); printPayslip(r.id, period, companyName); }}>명세서</button>
                </td>
              </tr>
            );
          })}
        </tbody>
        {/* 합계 행 */}
        <tfoot>
          <tr style={{ borderTop: '1px solid var(--border)', fontWeight: 900 }}>
            <td style={td('left', 'var(--text)', 900)}>합계</td>
            <td style={td('right', 'var(--text-sub)', 900)}>{fmt(totals.fixedBase)}</td>
            <td style={td('right', '#2563eb', 900)}>{fmt(totals.inc)}</td>
            <td style={td('right', '#dc2626', 900)}>{fmt(totals.attDed)}</td>
            <td style={td('right', '#dc2626', 900)}>{fmt(totals.whDed)}</td>
            {showUnusedAnnual && <td style={td('right', '#047857', 900)}>{fmt(totals.unusedAnnual)}</td>}
            <td style={td('right', '#16a34a', 900)}>{fmt(totalEarn)}</td>
            <td />
            <td style={td('right', '#dc2626', 900)}>{fmt(totalDed)}</td>
            <td style={td('right', 'var(--std-accent-text, #1d7de6)', 900)}>{fmt(totalNet)}</td>
            <td /><td />
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
}

// ── 뷰포트 크기에 따라 카드/테이블 자동 전환 ────────────────────────────────
export default function PayrollRecordList({ isMobile, records, period, totalEarn, totalDed, totalNet, companyName, onSelect }) {
  const shared = { records, period, totalEarn, totalDed, totalNet, companyName, onSelect };
  return isMobile ? <MobileRecordCards {...shared} /> : <DesktopRecordTable {...shared} />;
}
